import { useReducerCartMiddleware } from "modules/cart/middlewares/cartReducerMiddleware";
import { ReactNode, createContext, useState, useCallback } from "react";
import { cartReducer } from "modules/cart/reducers/cartReducer";
import { cartInitialState } from "modules/cart/reducers/cartReducer";

import { CartItem, CartActionType, Payload, ReplaceCartPayload, ValidationConflict, DecisionPayload } from "modules/cart/types/cart";
import { ProductDecision } from "modules/checkout/types/productDecission";
import { ListExecution, ResolvedListItem } from "modules/lists/types/lists";
import { AddCartMethod } from "modules/cart/types/add-cart-methods";
import { UserData } from "modules/authentication/types/user-data";
import { migrateCart } from "modules/cart/services/migrateCart";

const getUserData = (): UserData => {
    try {
        const data = localStorage.getItem("userData");
        if (!data) return {} as UserData;
        
        const parsed = JSON.parse(data) as UserData;
        return parsed;
    } catch (error) {
        console.warn("Error parsing userData from localStorage:", error);
        return {} as UserData;
    }
};

interface CartProviderProps {
    children: ReactNode;
}

interface CartContextType{
    cart:Array<CartItem>,
    addDeletefromCart:(payload:Payload)=>void,
    removeFromCart:(payload:Payload)=>void,
    clearCart:(  )=>void,
    recalculatePrice:(payload:Payload)=>void,
    replaceCart:(payload:ReplaceCartPayload)=>void,
    validating:(payload:Payload)=>Promise<ValidationConflict>,    
    refreshCart:(cart:CartItem[])=>void,
    setProductDecision:(id:string, productDecision:ProductDecision)=>void,
    resolutionExecution: ListExecution | null;
    setResolutionMode: (execution: ListExecution) => void;
    clearResolutionMode: () => void;
    /**
     * Applies list execution: resolved items go to cart directly, unresolved become pending skeletons.
     * Returns the inserted items keyed by both `product_id` (catalog id) and `sku` so callers
     * can build the backend bulk-update contract `{ location_id, items: [{ product_id, store_id? }] }`.
     * @param execution - The list execution result from the backend.
     * @param extraPlaceholders - Optional skeleton CartItem[] for temporary items.
     */
    applyListExecution: (execution: ListExecution) => Promise<{ product_id: string; sku: string }[]>;
    /** True while any linked item insertion fetch is in flight. */
    isInsertingItems: boolean;
    /** Number of linked item insertions currently in flight. */
    insertingCount: number;
    /**
     * Wraps addDeletefromCart with insertion tracking.
     */
    addDeletefromCartWithTracking: (payload: Payload) => Promise<string | null>;
    /** True while cart migration is in flight. */
    isMigrating: boolean;
    /** Triggers cart UUID migration. */
    migrateCart: (version: number, products: CartItem[]) => Promise<void>;
}

export const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider:React.FC<CartProviderProps> = ({children}) => {
    const {
        state,
        dispatch,
        addDeletefromCart,       
        removeFromCart,
        clearCart,
        recalculatePrice,
        replaceCart,
        validating,
        refreshCart,
        setProductDecision,
    } = useCartReducer();

    // Resolution mode is UI-only state
    const [resolutionExecution, setResolutionExecution] = useState<ListExecution | null>(null);
    const setResolutionMode = (execution: ListExecution) => setResolutionExecution(execution);
    const clearResolutionMode = () => setResolutionExecution(null);

    // Insertion tracking
    const [insertingCount, setInsertingCount] = useState(0);

    /**
     * Wraps addDeletefromCart with insertion-in-flight tracking.
     */
    const addDeletefromCartWithTracking = useCallback(async (payload: Payload): Promise<string | null> => {
        setInsertingCount((n) => n + 1);
        try {
            await addDeletefromCart(payload);
            return payload.sku ?? null;
        } finally {
            setInsertingCount((n) => n - 1);
        }
    }, [addDeletefromCart]);

    // Migration state
    const [isMigrating, setIsMigrating] = useState(false);

    /**
     * Triggers cart migration from legacy integer IDs to UUID format.
     */
    const migrateCartFn = useCallback(
      async (version: number, products: CartItem[]) => {
        setIsMigrating(true);
        try {
          const result = await migrateCart(version, products);
          if (result.status === "migrated" && result.products) {
            replaceCart({ cart: result.products });
          }
          const userData = getUserData();
          localStorage.setItem("v", userData.v?.toString() ?? "1");
        } finally {
          setIsMigrating(false);
        }
      },
      [replaceCart]
    );

    /**
     * Applies a list execution result to the cart.
     * All resolved and linked items (those with catalogProductId + sku) enter the cart
     * as real items via addDeletefromCartWithTracking(). Items without a product match
     * are silently skipped — no placeholders.
     *
     * Returns the inserted items as `{ product_id, sku }[]` so callers can build the
     * backend bulk-update contract without re-querying the execution.
     */
    const applyListExecution = async (execution: ListExecution): Promise<{ product_id: string; sku: string }[]> => {
        setResolutionExecution(execution);
        return collectInsertedItems(execution, addDeletefromCartWithTracking);
    };

    return (
        <CartContext.Provider value={{
            cart: state,
            addDeletefromCart, 
            removeFromCart,                
            clearCart,
            recalculatePrice,
            replaceCart,
            validating,    
            refreshCart,
            setProductDecision,
            resolutionExecution,
            setResolutionMode,
            clearResolutionMode,
            applyListExecution,
            isInsertingItems: insertingCount > 0,
            insertingCount,
            addDeletefromCartWithTracking,
            isMigrating,
            migrateCart: migrateCartFn,
        }}>
            {children}
        </CartContext.Provider>
    )
}

/**
 * Pure helper that resolves a list execution into the inserted items.
 * Dedupes by `catalogProductId`, sums quantities, and awaits each cart
 * insertion through `insertOne`. Items missing either `catalogProductId`
 * or `sku` are silently skipped.
 *
 * Exported for unit testing and to keep `applyListExecution` composable.
 * @param execution - The list execution result from the backend.
 * @param insertOne - Inserter used to push each deduped item into the cart.
 * @returns Array of inserted items with both `product_id` and `sku`.
 */
export const collectInsertedItems = async (
    execution: ListExecution,
    insertOne: (payload: Payload) => Promise<string | null>,
): Promise<{ product_id: string; sku: string }[]> => {
    // Collect all items (regular + temporary) that have a resolved product
    const allItems = [
        ...execution.itemsWithCandidates,
        ...execution.temporaryItemsWithCandidates,
    ];

    // Only process items with a real product match (catalogProductId + sku)
    const resolvedItems = allItems.filter(
        (item) => item.catalogProductId && item.sku,
    );

    if (resolvedItems.length === 0) return [];

    // Deduplicate by catalogProductId
    const deduped = new Map<string, { item: ResolvedListItem; quantity: number }>();
    for (const item of resolvedItems) {
        const pid = item.catalogProductId!;
        const existing = deduped.get(pid);
        deduped.set(pid, {
            item,
            quantity: (existing?.quantity ?? 0) + (item.quantity ?? 1),
        });
    }

    // Fire insertOne for each unique product
    const inserted: { product_id: string; sku: string }[] = [];
    for (const [, { item, quantity }] of deduped) {
        const sku = await insertOne({
            id: item.catalogProductId!,
            sku: item.sku!,
            name: item.name ?? undefined,
            category_id: item.categoryId ?? 0,
            quantity,
            is_ai_resolved: item.type === "free",
            raw_description: item.rawDescription,
        });
        if (sku) inserted.push({ product_id: item.catalogProductId!, sku });
    }
    return inserted;
};

export const useCartReducer = () => {
    const [state, dispatch] = useReducerCartMiddleware(cartReducer, cartInitialState);

    const addDeletefromCart = (payload: Payload): Promise<void> => {
        return dispatch({
            type: CartActionType.AddDeletefromCart,
            payload: payload,
        }) as Promise<void>;
    }

    const removeFromCart = (payload:Payload) => {
        dispatch({
            type: CartActionType.RemoveFromCart,
            payload: payload
        });
    }

    const clearCart = () => {
        dispatch({
            type: CartActionType.ClearCart
        });
    }

    const refreshCart = (cart:CartItem[]) => {
        dispatch({
            type: CartActionType.RefreshCart,
            payload:cart
        });
    }

    const recalculatePrice = (payload:Payload) => {        
        dispatch({
            type: CartActionType.Recalculate,
            payload: payload
        });
    }

    const replaceCart = (payload:ReplaceCartPayload) => {
        dispatch({
            type: CartActionType.ReplaceCart,
            payload: payload
        });
    }

    const validating = async (payload:Payload): Promise<ValidationConflict> => {
        const validationConflict = await dispatch({
            type: CartActionType.Validating,
            payload: payload
        });
        return validationConflict as ValidationConflict;
    }

    const setProductDecision = (id: string, productDecision: ProductDecision) => {
        dispatch({
            type: CartActionType.SetDecision,
            payload: { id, productDecision } as DecisionPayload,
        });
    };

    return {state, dispatch, addDeletefromCart, removeFromCart, clearCart, recalculatePrice, replaceCart, validating, refreshCart, setProductDecision};
}
